import Link from "next/link";

import { Icon, Anchor, Button } from "@newsela/angelou";
import PropTypes from "prop-types";

import {
  withTrackEvent,
  objectTypes,
  actionNames,
  actionPrefixes,
} from "@newsela/snitch";
import { OWN_ORIGIN } from "@/base/base.constants";
import { MONOLITH_ORIGIN } from "@/monolithIntegration/monolithIntegration.constants";
import CreateTextSetButton from "../CreatedByYou/CreateTextSetButton";
import {
  createTextSetButtonCss,
  createdByYouMessageWrapperCss,
  buttonRootCss,
  messageCss,
  wrapperCss,
} from "./NoContentReturned.style";
import NoContentReturnedSVG from "./NoContentReturned.svg.jsx";

const AnchorWithTrackEvent = withTrackEvent(Anchor);

const propTypes = {
  selector: PropTypes.string,
  isLiteUser: PropTypes.bool,
};

const CreatedByYouMessage = ({ isLiteUser }) => {
  return (
    <>
      Once you’ve created a text set, it will show up here.
      <div css={createdByYouMessageWrapperCss}>
        (To find assignments you’ve made instead, visit the&nbsp;
        <AnchorWithTrackEvent
          actionName={actionNames.navigate}
          actionPrefix={actionPrefixes.initiate}
          href={`${MONOLITH_ORIGIN}/binder/assignments`}
          isInline
          objectType={objectTypes.assignment}
        >
          assignments page
        </AnchorWithTrackEvent>
        )
      </div>
      <CreateTextSetButton
        selector="created-by-you"
        buttonProps={{
          size: Button.sizes.lg,
          __cssFor: {
            root: buttonRootCss,
          },
          disabled: isLiteUser,
        }}
        __cssFor={{
          root: createTextSetButtonCss,
        }}
      >
        Create text set
      </CreateTextSetButton>
    </>
  );
};
CreatedByYouMessage.propTypes = propTypes;

const AtYourSchoolMessage = ({ selector, isLiteUser }) => {
  return (
    <>
      Let’s collab! When teachers at your school have added text sets—you’ll
      find them here.
      <>
        <CreateTextSetButton
          selector="at-your-school"
          buttonProps={{
            size: Button.sizes.lg,
            __cssFor: {
              root: buttonRootCss,
            },
            disabled: isLiteUser,
          }}
          __cssFor={{
            root: createTextSetButtonCss,
          }}
        >
          Create text set
        </CreateTextSetButton>
        <Link href={OWN_ORIGIN} passHref legacyBehavior>
          <AnchorWithTrackEvent
            actionName={actionNames.navigate}
            streamProperties={{
              description: `No content returned on ${selector} page`,
            }}
          >
            Visit homepage
          </AnchorWithTrackEvent>
        </Link>
      </>
    </>
  );
};
AtYourSchoolMessage.propTypes = propTypes;

export const getMessageBySelector = ({ selector, isLiteUser = false }) => {
  const options = {
    "saved-by-you":
      "Ready to start a collection of saved content? Visit the homepage to find (and save!) articles, videos, and more.",
    "created-by-you": <CreatedByYouMessage isLiteUser={isLiteUser} />,
    "at-your-school": (
      <AtYourSchoolMessage selector="at-your-school" isLiteUser={isLiteUser} />
    ),
    "promoted-at-your-district":
      "Your district has not yet identified any Newsela resources to be promoted for your associated grade levels or subject areas.",
    "promoted-at-your-school":
      "Your school has not yet identified any Newsela resources to be promoted for your associated grade levels or subject areas.",
  };
  return options[selector];
};

export const NoContentReturned = ({ selector, isLiteUser }) => {
  return (
    <div css={wrapperCss}>
      <Icon
        SvgComponent={NoContentReturnedSVG}
        __cssFor={{
          /* ANGELOU FUTURE TODO: update angelou `Icon` to angelou `Pictogram`. See
            https://newsela.slack.com/archives/CD0NZL96E/p1680731684631809?thread_ts=1680731068.120409&cid=CD0NZL96E
            for more information. */
          root: { width: "240px" },
        }}
      />
      <div css={messageCss}>
        {getMessageBySelector({ selector, isLiteUser })}
      </div>
      {selector !== "created-by-you" && selector !== "at-your-school" ? (
        <Link href={OWN_ORIGIN} passHref legacyBehavior>
          <AnchorWithTrackEvent
            actionName={actionNames.navigate}
            streamProperties={{
              description: `No saved content returned on ${selector} page`,
            }}
            styleAnchorAsButton={{
              flavor: Button.flavors.primary,
            }}
          >
            Visit homepage
          </AnchorWithTrackEvent>
        </Link>
      ) : null}
    </div>
  );
};

NoContentReturned.propTypes = propTypes;
export default NoContentReturned;
